import { useSearchParams } from "react-router-dom";
import { MessageSquare, Package, ShieldCheck, MapPin } from "lucide-react";
import { SEO } from "../components/SEO";
import { Reveal } from "../components/Reveal";
import { EnquiryForm } from "../components/EnquiryForm";
import { getProduct, OFFICES } from "../data/content";

export default function Contact() {
  const [params] = useSearchParams();
  const productParam = params.get("product") || "";
  const product = getProduct(productParam);

  return (
    <>
      <SEO
        title="Get in Touch | ZasyaOnline"
        description="Contact ZasyaOnline about our products — MoveOS, myBantu, LearnSphere, CraneSoftwares — or our consulting and solutions practice. Every enquiry reaches the right team."
        path="/contact"
      />
      <section className="pt-28 pb-16 sm:pt-36 sm:pb-20" data-testid="contact-page">
        <div className="container-x grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <span className="eyebrow">Get in Touch</span>
              <h1 className="mt-5 font-display text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
                {product ? `Let's talk about ${product.name}.` : "Start the conversation."}
              </h1>
              <p className="mt-5 text-lg leading-relaxed text-slate-600">
                Tell us what you're trying to solve and the right team will respond —
                product specialists for product enquiries, consultants for everything
                else.
              </p>
            </Reveal>
            <div className="mt-10 space-y-4">
              {[
                {
                  icon: MessageSquare,
                  title: "One form, right team",
                  detail:
                    "We deliberately don't publish phone numbers or email addresses. This form routes your enquiry directly to the people who can act on it.",
                },
                {
                  icon: Package,
                  title: "Product demos",
                  detail:
                    "For a guided walkthrough of MoveOS, myBantu, LearnSphere or CraneSoftwares, use the Book a Demo option on any product page.",
                },
                {
                  icon: ShieldCheck,
                  title: "Your data, respected",
                  detail:
                    "Details you submit are used only to respond to your enquiry, as described in our Privacy Policy.",
                },
              ].map((f) => (
                <Reveal key={f.title}>
                  <div className="flex gap-4 rounded-2xl border border-slate-200 bg-white p-5">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
                      <f.icon size={18} aria-hidden="true" />
                    </span>
                    <div>
                      <h2 className="font-display text-sm font-bold text-slate-900">{f.title}</h2>
                      <p className="mt-1 text-xs leading-relaxed text-slate-600">{f.detail}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
              <Reveal>
                <div className="flex gap-4 rounded-2xl border border-slate-200 bg-white p-5" data-testid="contact-offices">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-teal-50 text-teal-700">
                    <MapPin size={18} aria-hidden="true" />
                  </span>
                  <div className="grid flex-1 gap-4 sm:grid-cols-2">
                    {OFFICES.map((o) => (
                      <a
                        key={o.label}
                        href={o.mapsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group/office block rounded-xl p-2 -m-2 transition-colors hover:bg-slate-50"
                        data-testid={`contact-office-${o.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      >
                        <h2 className="font-display text-sm font-bold text-slate-900 group-hover/office:text-blue-700">
                          {o.label}
                        </h2>
                        {o.lines.map((l) => (
                          <p key={l} className="mt-1 text-xs leading-relaxed text-slate-600">
                            {l}
                          </p>
                        ))}
                        <span className="mt-1.5 inline-block text-[11px] font-semibold text-teal-700">
                          Open in Google Maps →
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
          <Reveal delay={0.1} className="lg:col-span-7">
            <EnquiryForm defaultProduct={productParam} key={productParam} />
          </Reveal>
        </div>
      </section>
    </>
  );
}
